import _ from 'lodash';

import {
  EACH_CASE_SAME_RETURN_TYPE_ERROR,
  INCORRECT_SWITCH_ARGUMENTS_ERROR,
} from 'constants/expressions';
import { MAX_INTEGER_VALUE } from 'constants/number';
import {
  ARRAY_FUNCTION_ENUM,
  ARRAY_FUNCTIONS,
  ARRAY_FUNCTIONS_WITH_ITERATORS,
  FUNCTION_BRACKETS,
  ITERATOR_REGEX,
  LEX_TOKEN_TYPES,
  NUMERIC_TYPES,
  OPERATORS_PRIORITY,
  REGEXP,
  STRING_TYPES,
  SWITCH_FUNCTION_ENUM,
} from 'constants/parser';
import { jsonStringify } from 'utils/json';

import { canonicalizeFieldPath, getArrayElementEntity, getSchemaValue } from '../schemaPath';

import { getErrorPositionInfo } from './errorHandling';

const primitiveValuesMap = _.keyBy([
  ...NUMERIC_TYPES,
  ...STRING_TYPES,
  LEX_TOKEN_TYPES.bool,
  LEX_TOKEN_TYPES.date,
]);

export const findTokenByType = (tokens, type) => {
  let openedBracket = 0;
  return tokens.findIndex(({ value, type: curType }) => {
    if (value === FUNCTION_BRACKETS[0]) {
      openedBracket++;
    }

    if (value === FUNCTION_BRACKETS[1]) {
      openedBracket--;
    }

    return openedBracket === 0 && curType === type;
  });
};

const recFindTokenByConditionFunc = (token, conditionFunc) => {
  if (conditionFunc(token)) {
    return token;
  } else if (token.operands) {
    for (const operand of token.operands) {
      return recFindTokenByConditionFunc(operand, conditionFunc);
    }
  }

  return null;
};

export const findRootTokenIndex = tokens => {
  let openedBracket = 0;
  let maxPriority = -1;
  let maxPriorityIndex = -1;

  const tempTokens = [];

  const functionsStack = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = { ...tokens[i] };
    if (token.value === FUNCTION_BRACKETS[0]) {
      openedBracket++;
      if (tokens[i - 1]?.type === 'function') {
        functionsStack.push({ index: i - 1 });
      }
    }
    if (token.value === FUNCTION_BRACKETS[1]) {
      openedBracket--;
      if (functionsStack.length) {
        functionsStack.pop();
      }
    }

    tempTokens.push({
      ...token,
      callDepth: FUNCTION_BRACKETS.includes(token.value)
        ? Number.MAX_SAFE_INTEGER
        : functionsStack.length,
      tokensIndex: i,
    });
  }

  const minDepth = Math.min(...tempTokens.map(({ callDepth }) => callDepth));

  const minDepthTokens = tempTokens.filter(({ callDepth }) => callDepth === minDepth);

  minDepthTokens.forEach(({ value, type }, i) => {
    let currentPriority = OPERATORS_PRIORITY.find(({ regex }) =>
      new RegExp(regex).test(value),
    )?.priority;

    if (type === 'function' && currentPriority < 3) {
      currentPriority = 3;
    }

    if (maxPriority < currentPriority) {
      maxPriority = currentPriority;
      maxPriorityIndex = i;
    }
  });

  return openedBracket === 0 ? minDepthTokens[maxPriorityIndex]?.tokensIndex : -1;
};

const findArrayEntityByPath = ({ entitiesSchema, path }) =>
  (path || '').split('.').reduce((acc, keyName) => {
    if (_.isString(acc)) {
      return _.get(entitiesSchema, acc ? `${acc}.${keyName}` : keyName, '');
    }

    if (_.isPlainObject(acc)) {
      return _.get(acc, keyName, '');
    }

    return acc;
  }, '');

export const getPathToRootArray = ({ entitiesSchema, arrayToken }) => {
  const arrayType = getSchemaValue({
    entitiesSchema,
    path: canonicalizeFieldPath({ entitiesSchema, path: arrayToken?.value }),
  });

  // Interpolated into a dotted path by both callers, where a nullish entity must read as an
  // empty segment rather than the string "null".
  return getArrayElementEntity(arrayType) ?? '';
};

export const separateTokens = tokens => {
  const arr = [];

  const separate = tokens => {
    const index = findTokenByType(tokens, LEX_TOKEN_TYPES.separator);

    if (index >= 0) {
      arr.push(tokens.slice(0, index));
      separate(tokens.slice(index + 1));
    } else {
      arr.push(tokens);
    }
  };
  separate(tokens);

  return arr;
};

const getIteratorName = (arrayPath = '', entities) => {
  let depth = 0;
  const lastIndex = arrayPath.toLowerCase().split('.').pop();
  const iteratorName = lastIndex.endsWith('s') ? `_${lastIndex.slice(0, -1)}` : `_${lastIndex}`;

  while (entities?.[`${iteratorName}${depth > 0 ? depth : ''}`]) {
    depth = depth + 1;
  }

  return depth > 0 ? `${iteratorName}${depth}` : iteratorName;
};

export const getIteratorToken = (parentArray, parentTree) => {
  let arr;

  if (parentArray) {
    arr = parentArray.find(
      ({ type, typeOfValue }) =>
        type === LEX_TOKEN_TYPES.field && typeOfValue === LEX_TOKEN_TYPES.array,
    );
  } else if (parentTree) {
    arr = recFindTokenByConditionFunc(
      parentTree,
      token => token.type === LEX_TOKEN_TYPES.field && token.typeOfValue === LEX_TOKEN_TYPES.array,
    );
  }

  return {
    type: 'value',
    typeOfValue: 'iterator',
    value: getIteratorName(arr?.value),
    parent: arr?.parent,
  };
};

const getAggregationFunctionsArgs = args => {
  const { 1: array, 3: boolean } = args;
  return { 1: array, 2: boolean };
};

export const getFormattedRootFields = (schemaParams = {}) => {
  const fields = Object.entries(_.pick(schemaParams.entities, schemaParams.rootEntities));
  return fields.reduce((acc, [field, type]) => {
    if (schemaParams.entities[type] || _.isObject(type)) {
      acc.push({ key: 'Fields', value: field, name: `${field} (entity)` });
    } else if (schemaParams.enumTypes[type]) {
      acc.push({ key: 'Fields', value: field, name: `${field} (enum)` });
    } else if (type.startsWith(LEX_TOKEN_TYPES.array)) {
      acc.push({ key: 'Fields', value: field, name: `${field} (array)` });
    } else {
      acc.push({ key: 'Fields', value: field, name: `${field} (${type})` });
    }
    return acc;
  }, []);
};

export const validateTree = ({
  schemaParams: schemaParamsProp,
  data,
  isOnlyBooleanAllowed = true,
  isOnlyNumericAllowed = false,
}) => {
  let validationError;
  let fields;
  let schemaParams = _.cloneDeep(schemaParamsProp);
  const iteratorNames = {};

  const getFormattedFields = (possibleFieldsArray, isArrayEntity) => {
    const possibleFields = Object.fromEntries(possibleFieldsArray);
    const allPossibleFields = [];

    const checkChildFields = (fields, keys = [], visited = new Set()) => {
      if (_.isObject(fields)) {
        const fieldsAsArray = Object.entries(fields);
        fieldsAsArray.forEach(([key, value]) => {
          let field;
          const pathArray = [...keys, key];
          const fieldName = pathArray.join('.');

          if (_.isObject(value)) {
            field = { key: 'Fields', value: fieldName, name: `${fieldName} (entity)` };
            allPossibleFields.push(field);
            checkChildFields(value, pathArray, visited);
          } else if (_.isObject(possibleFields[value]) && !visited.has(value)) {
            const childVisited = new Set(visited);
            childVisited.add(value);
            checkChildFields(possibleFields[value], pathArray, childVisited);
          } else {
            if (schemaParams.enumTypes[value] && !isArrayEntity) {
              field = { key: 'Fields', value: fieldName, name: `${fieldName} (enum)` };
            } else if (value?.startsWith(LEX_TOKEN_TYPES.array)) {
              field = { key: 'Fields', value: fieldName, name: `${fieldName} (array)` };
            } else if (!isArrayEntity) {
              field = { key: 'Fields', value: fieldName, name: `${fieldName} (${value})` };
            }

            if (field) {
              allPossibleFields.push(field);
            }
          }
        });
      }
    };
    checkChildFields(possibleFields);
    return allPossibleFields;
  };

  const validateIterator = iterator => new RegExp(ITERATOR_REGEX).test(iterator);

  const checkField = (parent, child) => {
    if (!child && parent?.type !== 'operator') {
      return (validationError = `Check expression ${getErrorPositionInfo(
        parent?.position?.start,
        parent?.position?.end,
      )}`);
    }

    if (ARRAY_FUNCTIONS_WITH_ITERATORS.includes(parent?.value) && parent?.operands[1] === child) {
      if (!validateIterator(child.value)) {
        return (validationError = `Iterator ${
          child.value
        } identifier must start with a letter, or _, contain Unicode letter characters, decimal digit characters, Unicode connecting characters, Unicode combining characters, or Unicode formatting characters ${getErrorPositionInfo(
          child?.position?.start,
          child?.position?.end,
        )}`);
      }

      child.typeOfValue = 'iterator';
      child.type = 'value';

      const arrayEntity = parent.arrayEntity || parent.operands[0].arrayEntity;
      const arrayEntityEntity = schemaParams.entities[arrayEntity];

      if (schemaParams.entities[child.value]) {
        const baseIteratorName = child.value;

        iteratorNames[baseIteratorName] = (iteratorNames[baseIteratorName] || 0) + 1;
        child.value = `${baseIteratorName}${iteratorNames[baseIteratorName]}`;
      }

      if (Object.keys(schemaParams.entities).includes(child.value)) {
        return (validationError = `${
          child.value
        } iterator name is already taken. ${getErrorPositionInfo(
          child?.position?.start,
          child?.position?.end,
        )}`);
      }

      schemaParams.entities[child.value] = arrayEntityEntity
        ? _.cloneDeep(arrayEntityEntity)
        : parent.arrayEntity;

      schemaParams.rootEntities.unshift(child.value);
      return null;
    }

    if (parent?.type === 'operator') {
      if (
        schemaParams.enumTypes[parent?.operands[0]?.typeOfValue]?.includes(
          parent?.operands[1]?.value,
        )
      ) {
        child.typeOfValue = parent.operands[0].typeOfValue;
      }

      if (
        schemaParams.enumTypes[parent?.operands[1]?.typeOfValue]?.includes(
          parent?.operands[0]?.value,
        )
      ) {
        child.typeOfValue = parent.operands[1].typeOfValue;
      }
    }

    if (
      LEX_TOKEN_TYPES.field === child?.type ||
      (ARRAY_FUNCTIONS.includes(parent?.value) && parent?.operands[0] === child)
    ) {
      // Only a field token carries a path. operands[0] of an array function can be a nested array
      // function, whose value is a function name — re-spelling that renames the function.
      if (LEX_TOKEN_TYPES.field === child?.type) {
        child.value = canonicalizeFieldPath({
          entitiesSchema: schemaParams.entities,
          path: child.value,
          arrayEntity: child?.arrayEntity,
        });
      }

      child.value.split('.').reduce((prevPath, field, index) => {
        // A segment that failed to resolve leaves the error text as the accumulator, so walking on
        // re-fails on the next segment and blames it instead of the one actually wrong.
        if (validationError) {
          return prevPath;
        }

        const path = prevPath ? `${prevPath}.${field}` : field;
        const newPath = getSchemaValue({ entitiesSchema: schemaParams.entities, path });

        const returnedPath = _.isObject(newPath) ? path : newPath;

        const correctFields = Object.entries(
          prevPath
            ? getSchemaValue({
                entitiesSchema: schemaParams.entities,
                path: prevPath,
                defaultValue: {},
              })
            : _.pick(schemaParams.entities, schemaParams.rootEntities),
        );

        // Check parent - if it is ARRAY FUNCTION - it means child has to be array.
        // Check it for last index in loop
        if (
          ARRAY_FUNCTIONS.includes(parent?.value) &&
          child === parent.operands[0] &&
          child.value.split('.').length === index + 1
        ) {
          // Set here new closure name
          const pathToRootArray = child.arrayEntity
            ? returnedPath.replace(LEX_TOKEN_TYPES.array, '').slice(1, -1)
            : parent?.operands[0].value;

          const arrayEntity = findArrayEntityByPath({
            entitiesSchema: schemaParams.entities,
            path: pathToRootArray,
          });

          const formattedArrayEntity = _.isObject(arrayEntity)
            ? pathToRootArray
            : arrayEntity.replace(LEX_TOKEN_TYPES.array, '').slice(1, -1);

          if (parent) {
            parent.arrayEntity = formattedArrayEntity;
          }

          if (parent.value === ARRAY_FUNCTION_ENUM.WHERE) {
            parent.typeOfValue = LEX_TOKEN_TYPES.array;
          }

          child.typeOfValue = LEX_TOKEN_TYPES.array;

          if (!_.isString(newPath) || !newPath.startsWith(LEX_TOKEN_TYPES.array)) {
            const possibleArrayFields = getFormattedFields(
              correctFields,
              ARRAY_FUNCTIONS.includes(parent?.value),
            );

            fields = possibleArrayFields;

            if (child.typeOfValue === LEX_TOKEN_TYPES.array) {
              return null;
            }

            if (child.value.split('.').length <= 1) {
              return (validationError = `Wrong type ${
                child.value
              } - it should be array. ${getErrorPositionInfo(
                child?.position?.start,
                child?.position?.end,
              )}`);
            }

            validationError = `Wrong type ${child.value} - it should be array. Fields: ${
              possibleArrayFields.length
                ? possibleArrayFields.map(({ name }) => name).join(', ')
                : 'No appropriate fields for this entity'
            } ${getErrorPositionInfo(child?.position?.start, child?.position?.end)}`;
          }

          return null;
        }

        if (!newPath) {
          const fieldsWithTypes = getFormattedFields(correctFields);

          fields = fieldsWithTypes;

          if (child.value.split('.').length <= 1) {
            return (validationError = `Wrong path, ${child.value} (${field}) ${getErrorPositionInfo(
              child?.position?.start,
              child?.position?.end,
            )}`);
          }

          return (validationError = `Wrong path, ${child.value} (${field}) Fields: ${fieldsWithTypes
            .map(({ name }) => name)
            .join(', ')} ${getErrorPositionInfo(child?.position?.start, child?.position?.end)}`);
        }

        if (child.value.split('.').length === index + 1) {
          child.typeOfValue = newPath;
        }

        return returnedPath;
      }, child?.arrayEntity);

      if (!validationError && !child.parent && isOnlyBooleanAllowed) {
        return (validationError = `Incorrect expression ${child.value} ${getErrorPositionInfo(
          child?.position?.start,
          child?.position?.end,
        )}`);
      }
    }

    if (LEX_TOKEN_TYPES.field === child?.type) {
      const [rootFieldValue, secondFieldValue] = child.value.split('.');
      const isRootEntityIncluded = schemaParams.rootEntities.includes(rootFieldValue);
      const isRootEntityIncludedWithNonBooleanAllowed = isOnlyBooleanAllowed
        ? isRootEntityIncluded
        : isRootEntityIncluded && secondFieldValue;
      const isEntityIncluded = !!schemaParams.entities[rootFieldValue];

      if (_.isString(secondFieldValue) && !isRootEntityIncluded) {
        fields = [];
      }

      const isValid = child.spec_field
        ? isEntityIncluded
        : isRootEntityIncludedWithNonBooleanAllowed;

      if (!isValid) {
        return (validationError = `Incorrect expression ${child.value} ${getErrorPositionInfo(
          child?.position?.start,
          child?.position?.end,
        )}`);
      }
    }
  };

  const checkFunction = (parent, child) => {
    if (child?.type === LEX_TOKEN_TYPES.function) {
      const {
        returnType,
        arguments: args = {},
        argumentsCount,
      } = schemaParams.functions[child.value];

      const requiredArgumentsCount = Object.values(args).filter(({ optional }) => !optional).length;

      const argsStringified = jsonStringify(
        ARRAY_FUNCTIONS_WITH_ITERATORS.includes(child.value)
          ? getAggregationFunctionsArgs(args)
          : args,
      );

      const functionArgumentsCount = ARRAY_FUNCTIONS.includes(child.value) ? 2 : argumentsCount;

      const isSwitchFunction = child.value === SWITCH_FUNCTION_ENUM.SWITCH;

      if (child.operands.length > argumentsCount) {
        return (validationError = `Function ${
          child.value
        } takes ${functionArgumentsCount} args (${argsStringified}) ${getErrorPositionInfo(
          child?.position?.start,
          child?.position?.end,
        )}`);
      }

      if (
        (ARRAY_FUNCTIONS.includes(child.value) && child.operands.length === 2) ||
        !child.operands[2] ||
        child.operands[2]?.type === 'operator'
      ) {
        child.operands?.[0]?.value?.split('.').reduce((prevPath, field) => {
          const path = prevPath ? `${prevPath}.${field}` : field;
          const newPath = getSchemaValue({ entitiesSchema: schemaParams.entities, path });

          const returnedPath = _.isObject(newPath) ? path : newPath;

          const correctFields = Object.entries(
            prevPath
              ? getSchemaValue({
                  entitiesSchema: schemaParams.entities,
                  path: prevPath,
                  defaultValue: {},
                })
              : _.pick(schemaParams.entities, schemaParams.rootEntities),
          );

          fields = getFormattedFields(correctFields);

          return returnedPath;
        }, '');
      }

      if (isSwitchFunction) {
        const isCorrectOperandsTypes = child.operands.every(
          ({ value }) => value === SWITCH_FUNCTION_ENUM.CASE,
        );

        if (!isCorrectOperandsTypes) {
          return (validationError = INCORRECT_SWITCH_ARGUMENTS_ERROR);
        }
      }

      child.operands.some((operand, index) => {
        const { type, lookups, valueMasks } = args[args.sampler ? 'sampler' : index + 1];
        const isFunctionType = !!schemaParams.functions[type];
        const typeToCheck = isFunctionType ? schemaParams.functions[type].returnType : type;

        // The 2nd condition is for entities
        const isConsistent =
          typeToCheck.split('|').includes(operand.typeOfValue) ||
          schemaParams.entities[operand.typeOfValue];

        const argumentNumber = index + 1;

        if (!isConsistent) {
          const type = args.sampler ? returnType : args[argumentNumber].type;

          return (validationError = `Function ${
            child.value
          } (${argumentNumber} arg) takes only ${type} type ${getErrorPositionInfo(
            child?.position?.start,
            child?.position?.end,
          )}`);
        }

        if (isFunctionType && isSwitchFunction) {
          const firstOperandTypeOfValue = child.operands[0].typeOfValue;
          const curOperandTypeOfValue = operand.typeOfValue;

          if (firstOperandTypeOfValue !== curOperandTypeOfValue) {
            return (validationError = EACH_CASE_SAME_RETURN_TYPE_ERROR);
          }
        }

        if (returnType === LEX_TOKEN_TYPES.date) {
          if (lookups === 'timeZones' && !schemaParams.lookups.timeZones.includes(operand.value)) {
            fields = schemaParams.lookups.timeZones.map(name => ({
              key: 'Timezones',
              name,
              value: name,
            }));

            return (validationError = `${
              child.value
            }: ${argumentNumber} argument is wrong timezone ${getErrorPositionInfo(
              child?.position?.start,
              child?.position?.end,
            )}`);
          } else if (
            valueMasks === 'date' &&
            !`'${operand.value}'`.match(new RegExp(REGEXP.date, 'gim'))
          ) {
            return (validationError = `${child.value}: ${argumentNumber} argument has to match ${
              schemaParams.valueMasks.date
            } ${getErrorPositionInfo(child?.position?.start, child?.position?.end)}`);
          }
        }

        return !isConsistent;
      });

      if (
        !validationError &&
        (child.operands.length < requiredArgumentsCount ||
          (!child.operands.length && requiredArgumentsCount))
      ) {
        return (validationError = `Function ${
          child.value
        } takes ${functionArgumentsCount} args (${argsStringified}) ${getErrorPositionInfo(
          child?.position?.start,
          child?.position?.end,
        )}`);
      }
    }
  };

  const checkOperator = (parent, child) => {
    if (!parent && !child.operands && isOnlyBooleanAllowed) {
      return (validationError = `Incorrect expression`);
    }

    if (child.type === LEX_TOKEN_TYPES.operator) {
      const firstOperandIsEnum = schemaParams.enumTypes[child.operands[0]?.typeOfValue];

      // Enums
      if (firstOperandIsEnum && !firstOperandIsEnum.includes(child.operands[1]?.value)) {
        const enumValues = Object.values(firstOperandIsEnum);

        fields = enumValues.map(name => ({
          key: 'Enum Types',
          name,
          value: name,
        }));

        validationError = `Wrong enum value ${child.operands[1]?.value}. Values: ${enumValues.join(
          ', ',
        )} ${getErrorPositionInfo(
          child?.operands[1]?.position?.start,
          child?.operands[1]?.position?.end,
        )}`;
      }

      // For some specific types and for simple (int|string|..., string, int, ...)
      const setOfAcceptableTypes =
        _.isString(child.operands[0]?.typeOfValue) && child.operands[0]?.typeOfValue.split('|');

      const isOperandsBelongToNumericTypes =
        NUMERIC_TYPES.includes(child.operands[0]?.typeOfValue) &&
        NUMERIC_TYPES.includes(child.operands[1]?.typeOfValue);

      const isOperandsBelongToStringTypes =
        STRING_TYPES.includes(child.operands[0]?.typeOfValue) &&
        STRING_TYPES.includes(child.operands[1]?.typeOfValue);

      const isValueTooLarge = +child.operands[1]?.value > MAX_INTEGER_VALUE;

      if (isOperandsBelongToNumericTypes && isValueTooLarge) {
        return (validationError = `Value is too large ${
          child.operands[1]?.value
        } ${getErrorPositionInfo(
          child?.operands[1]?.position?.start,
          child?.operands[1]?.position?.end,
        )}`);
      }

      // Other types
      if (
        !firstOperandIsEnum &&
        !setOfAcceptableTypes.includes?.(child.operands[1]?.typeOfValue) &&
        !(isOperandsBelongToNumericTypes || isOperandsBelongToStringTypes)
      ) {
        if (!child.operands[1]?.value && !child.operands[1]?.typeOfValue) {
          return (validationError = 'Operand types have to be equal');
        }

        return (validationError = `Operand types have to be equal (${child.operands[0]?.value} - ${
          child.operands[0]?.typeOfValue
        }, ${child.operands[1]?.value} - ${child.operands[1]?.typeOfValue}) ${getErrorPositionInfo(
          child?.operands[0]?.position?.start,
          child?.operands[1]?.position?.end,
        )}`);
      }

      // Implied that for specific types (int|double|..., string|entities) are equal - so I can take any of them to define available sign (setOfTypes[0])
      const signs = firstOperandIsEnum
        ? ['!=', '==']
        : schemaParams.commonTypes[setOfAcceptableTypes[0]];

      // Signs
      if (!signs?.includes(child?.value)) {
        return (validationError = `Wrong sign ${child.operands[0]?.value} ${child.value} ${
          child.operands[1]?.value
        } ${getErrorPositionInfo(
          child?.operands[0]?.position?.start,
          child?.operands[1]?.position?.end,
        )}`);
      }
    }
  };

  const operandsValidation = (parent, checkToken) => {
    parent?.operands?.forEach(child => {
      checkToken(parent, child);

      if (!validationError) {
        operandsValidation(child, checkToken);
      }
    });
  };

  [checkField, checkFunction, checkOperator].forEach(checkToken => {
    if (!validationError) {
      checkToken(null, data);
    }

    if (!validationError) {
      operandsValidation(data, checkToken);
    }
  });

  const isWhere = data?.value === ARRAY_FUNCTION_ENUM.WHERE;
  const isReturnTypeBoolean = data?.typeOfValue === LEX_TOKEN_TYPES.bool || isWhere;
  const isReturnTypeEnum = !!schemaParams.enumTypes[data?.typeOfValue];

  if (isOnlyBooleanAllowed && !isReturnTypeBoolean) {
    validationError = 'Expression can return only boolean value';
  }

  if (!isOnlyBooleanAllowed && !primitiveValuesMap[data?.typeOfValue] && !isReturnTypeEnum) {
    validationError = 'Expression can return only primitive value';
  }

  if (isOnlyNumericAllowed && !NUMERIC_TYPES.includes(data?.typeOfValue)) {
    validationError = 'Expression can return only numeric value';
  }

  const checkIterators = (expression, availableIterators = []) => {
    if (!expression) {
      return;
    }

    if (expression.value.startsWith('_') && _.toLower(expression.type) === LEX_TOKEN_TYPES.field) {
      const iteratorName = expression.value.split('.')[0];

      if (!availableIterators.includes(iteratorName)) {
        return `Iterator name ${iteratorName} is not available, choose one from [${availableIterators.join(
          ',',
        )}]`;
      }
    }

    let newAvailableIterators = [
      ...availableIterators,
      ...(ARRAY_FUNCTIONS.includes(expression.value) ? [expression.operands[1]?.value] : []),
    ];

    return expression.operands?.map(expression =>
      checkIterators(expression, newAvailableIterators),
    );
  };

  const iteratorsValidationError = _.flatMapDeep(checkIterators(data)).filter(Boolean)?.[0];

  if (iteratorsValidationError) {
    validationError = iteratorsValidationError;
  }

  return { validationError, fields };
};

export const minimizeTokens = token => {
  token?.operands?.forEach((child, index) => {
    token.operands[index] = _.omit(token.operands[index], ['parent', 'position', 'spec_field']);
    minimizeTokens(child);
  });
};
